import { IArmador } from './ArmadoresServices'
import { Ref } from 'vue'
import dataStorageManager, { BucketDefaultIDs, DMS_Operation } from '../../managers/data/DataStorageManager'
import dataQueryManager from '../../managers/network/DataQueryManager'
import { emailify, phoneify, rutify } from '../apiUtils'
import { IRequest } from './RequestsServices'
import { RequestedChange } from './ChangesServices'
import { IUser } from './UsersServices'

export type TrabajadorCargos = 'patron' | 'tripulante'

export const tripulanteReplaceRut = (rut: string): string => {
  switch (rut) {
    case '11111111-1':
      return '15077200-1'
    case '22222222-2':
      return '8163374-6'
    /*case '33333333-3':
      return '6357287-k'*/
    case '44444444-4':
      return '13650871-7'
    default:
      return rutify(rut)
  }
}

export interface ITripulante {
  id: number
  nombre_completo: string
  rut: string
  telefono: string
  email: string
  cargo: TrabajadorCargos
  imagen_perfil: number
  carnet_adjunto: number
  matricula_adjunta: number
  fecha_creacion: string
  fecha_actualizacion: string | null
  usuario_creacion: number
  activo: boolean
  id_matricula: number
  cancelado_definitivo?: boolean
  motivo_cancelacion?: string | null
}

export interface OperatedTripulante extends ITripulante {
  requestData: IRequest | undefined
  changesData: RequestedChange[]
  asociationData?: IRequest
  asociationsData: IRequest[]
  armadorData?: IArmador | undefined
  userData?: IUser | undefined
  // Cadena de búsqueda precomputada (nombre + rut + teléfono + correo + cargo, ya normalizada).
  // Se calcula una vez en el pipeline del hub para que el filtro "Todos los campos" no normalice
  // por ítem en cada pulsación. Ver TripulantesHub.setFullDataToTripulantes y normalizeSearch.
  _search?: string
}

export const defaultITripulante: ITripulante = {
  id: 0,
  nombre_completo: '',
  rut: '',
  telefono: '',
  email: '',
  cargo: 'tripulante',
  imagen_perfil: 0,
  carnet_adjunto: 0,
  matricula_adjunta: 0,
  fecha_creacion: '',
  fecha_actualizacion: '',
  usuario_creacion: 0,
  activo: false,
  id_matricula: 0,
  cancelado_definitivo: false,
  motivo_cancelacion: null,
}

export default class TripulantesServices {
  private bucketID: string | null = null

  constructor() {
    // Registrar el bucket para tripulantes
    this.bucketID = dataStorageManager.registerBucket<ITripulante>(
      this.getAllTripulantes.bind(this), // Asegúrate de enlazar el contexto correcto
      {
        refreshRatio: 5 * 60 * 1000, // Refrescar cada 5 minutos
        //preload: true, // Preload
        bucketID: BucketDefaultIDs.TRIPULANTES, // ID del bucket
        operations: new Map<string, DMS_Operation>([
          [
            'insertTripulante',
            {
              operation: this.insertTripulante.bind(this),
              reloadAfter: true,
            },
          ],
          [
            'updateTripulante',
            {
              operation: this.updateTripulante.bind(this),
              reloadAfter: true,
            },
          ],
        ]), // Las operaciones se añadiran despues
      },
    )
  }

  startup() {}

  async getAllTripulantes(): Promise<ITripulante[]> {
    try {
      const response = await dataQueryManager.addQuery<ITripulante | ITripulante[]>({
        query: `${import.meta.env.VITE_BASE_URL}/enrolamiento/1/op_select_tripulacion`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
      })
      if (response.data && Array.isArray(response.data)) {
        return response.data.map((tripulante) => {
          return {
            ...tripulante,
            rut: tripulanteReplaceRut(tripulante.rut.toString()),
            email: tripulante.email ? emailify(tripulante.email) : '',
            telefono: tripulante.telefono ? phoneify(tripulante.telefono) : '',
            imagen_perfil: typeof tripulante.imagen_perfil === 'number' ? tripulante.imagen_perfil : 0,
            carnet_adjunto: typeof tripulante.carnet_adjunto === 'number' ? tripulante.carnet_adjunto : 0,
            matricula_adjunta: typeof tripulante.matricula_adjunta === 'number' ? tripulante.matricula_adjunta : 0,
            id_matricula: typeof tripulante.id_matricula === 'number' ? tripulante.id_matricula : 0,
          }
        })
      } else if (response.data) {
        return [response.data]
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    }
    return []
  }

  async insertTripulante(
    tripulanteData: Omit<ITripulante, 'id' | 'fecha_creacion' | 'fecha_actualizacion' | 'activo'>,
  ): Promise<number | false> {
    try {
      const body = {
        op_insert_tripulacion: {
          nombre_completo: tripulanteData.nombre_completo,
          rut: rutify(tripulanteData.rut),
          telefono: phoneify(tripulanteData.telefono),
          email: emailify(tripulanteData.email),
          cargo: tripulanteData.cargo,
          imagen_perfil: tripulanteData.imagen_perfil,
          carnet_adjunto: tripulanteData.carnet_adjunto,
          matricula_adjunta: tripulanteData.matricula_adjunta,
          usuario_creacion: tripulanteData.usuario_creacion,
          id_matricula: tripulanteData.id_matricula,
        },
      }

      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/enrolamiento/1/op_insert_tripulacion`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body: body,
      })

      // op_insert_tripulacion devuelve el id generado (RETURNING): lo propagamos.
      if (response.data && typeof response.data === 'number') {
        return response.data
      }
    } catch (error) {
      console.error('Error inserting embarcacion:', error)
    }
    return false
  }

  async updateTripulante(tripulanteData: ITripulante): Promise<boolean> {
    try {
      const body = {
        op_update_tripulacion: {
          nombre_completo: tripulanteData.nombre_completo,
          rut: rutify(tripulanteData.rut),
          telefono: phoneify(tripulanteData.telefono),
          email: emailify(tripulanteData.email),
          cargo: tripulanteData.cargo,
          imagen_perfil: tripulanteData.imagen_perfil,
          carnet_adjunto: tripulanteData.carnet_adjunto,
          matricula_adjunta: tripulanteData.matricula_adjunta,
          activo: tripulanteData.activo,
          id_matricula: tripulanteData.id_matricula,
          autorizacion: 0,
          id: tripulanteData.id,
        },
      }

      const response = await dataQueryManager.addQuery<number>({
        query: `${import.meta.env.VITE_BASE_URL}/enrolamiento/1/op_update_tripulacion`,
        operation: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        params: [],
        body,
      })
      if (response.data && typeof response.data === 'number') {
        return true
      }
    } catch (error) {
      console.error('Error updating armador:', error)
    }
    return false
  }
}
